import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifyToken } from '../services/authService';

/**
 * Hook para verificação automática de sessão a cada 1 minuto.
 * Valida se a sessão do usuário ainda está ativa no backend.
 * Se a sessão estiver inválida, faz logout e redireciona para página inicial.
 *
 * @hook
 * @returns {void}
 *
 * @example
 * function App() {
 *   useSessionCheck();
 *   return <div>...</div>;
 * }
 */
export const useSessionCheck = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const intervalRef = useRef(null);

  useEffect(() => {
    /**
     * Verifica se a sessão está ativa no backend.
     * Se inválida, limpa localStorage e redireciona.
     * Não executa na página de reset de senha para evitar redirecionamento automático.
     */
    const checkSession = async () => {
      // Não verificar sessão na página de reset de senha
      if (location.pathname.startsWith('/reset-password/')) {
        return;
      }

      const user = localStorage.getItem('user');
      const accessToken = localStorage.getItem('accessToken');

      // Se não há usuário logado ou token, não faz nada
      if (!user || !accessToken) {
        return;
      }
      
      try {
        await verifyToken();
      } catch {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/', { replace: true });
        window.location.reload();
      }
    };

    // Executar verificação imediatamente
    checkSession();

    // Configurar intervalo de 5 minutos (300000ms) - mais eficiente
    intervalRef.current = setInterval(checkSession, 300000);

    // Cleanup: limpar intervalo ao desmontar
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [location.pathname]);
};
